import React, { type FC } from 'react';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';

import { useRealtimeAccessRequestsFlags } from './useRealtimeAccessRequestsFlags.tsx';

interface RealtimeAccessRequestsProps {
	contentType: string;
	contentId: string;
	pageTitle: string | null | undefined;
	onReviewRequestsClick: () => void;
}

interface RealtimeAccessRequestsInternalProps {
	userId: string;
	cloudId: string;
	edition: ConfluenceEdition.STANDARD | ConfluenceEdition.PREMIUM;
}
const RealtimeAccessRequestsInternal = withFlags<
	RealtimeAccessRequestsProps & RealtimeAccessRequestsInternalProps
>(
	({
		cloudId,
		userId,
		edition,
		contentType,
		contentId,
		pageTitle,
		flags,
		onReviewRequestsClick: onReviewRequests,
	}) => {
		useRealtimeAccessRequestsFlags({
			userId,
			cloudId,
			edition,
			onReviewRequests,
			contentId,
			contentType,
			pageTitle,
			flags,
		});

		return null;
	},
);

export const shouldRestrictAccess = (accessStatus: AccessStatus | null): boolean =>
	[
		null,
		AccessStatus.ANONYMOUS_ACCESS,
		AccessStatus.EXTERNAL_SHARE_ACCESS,
		AccessStatus.NOT_PERMITTED,
		AccessStatus.UNLICENSED_AUTHENTICATED_ACCESS,
	].includes(accessStatus);

export const RealtimeAccessRequestsComponent: FC<Omit<RealtimeAccessRequestsProps, 'flags'>> = (
	props,
) => {
	const { cloudId, userId, edition, isLoggedIn, accessStatus } = useSessionData();
	const isSSR = useIsSSROrHydrating();

	if (
		isSSR ||
		!cloudId ||
		!userId ||
		!edition ||
		edition === 'FREE' ||
		!isLoggedIn ||
		shouldRestrictAccess(accessStatus)
	) {
		return null;
	}

	return (
		<RealtimeAccessRequestsInternal
			userId={userId}
			cloudId={cloudId}
			edition={edition}
			{...props}
		/>
	);
};

export const RealtimeAccessRequests: React.FC<Omit<RealtimeAccessRequestsProps, 'flags'>> =
	withTransparentErrorBoundary({
		attribution: Attribution.PERMISSIONS_EXPERIENCE,
	})(RealtimeAccessRequestsComponent);
